import type { TitleOfferFragment, TitleOfferLightFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { getTitlePlayerUrl } from '@/features/title-player/helpers/title-player-url';
import { openJwTvRentalModal } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';

import { type PlayerModalTitle } from '@/features/title-player/types';
import type { WatchNowOfferFragment } from '@/pages/graphql/fragments/WatchNowOffer.fragment';
import { useRouter } from 'vue-router/composables';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';
import { transformContextToLinkToContext } from '@/helpers/tracking/tracking-helper';
import type { SnowplowContext, SnowplowLinkContext } from '@/helpers/tracking/providers';
import { S72ModalContexts } from '@/features/jwtv-titles-rental/tracking';

type VariantPromoInfo = {
	code: string;
	queryParam: string;
};

export function useRentalModal() {
	const router = useRouter();
	const { isAccountVerified } = storeToRefs(useUserStore());

	function openRentalModal({
		title,
		offer,
		additionalContexts,
		overrideRedirectUrl,
		expPromoCode,
		onClose,
	}: {
		title: PlayerModalTitle;
		offer: WatchNowOfferFragment | TitleOfferFragment | TitleOfferLightFragment;
		additionalContexts: SnowplowContext[];
		overrideRedirectUrl?: boolean;
		expPromoCode?: VariantPromoInfo | null;
		onClose?: () => void;
	}) {
		const linkContext = additionalContexts.find(
			(ctx): ctx is SnowplowLinkContext => (ctx as SnowplowLinkContext).__name === 'LinkContext'
		);
		const linkContextId = linkContext?.link?.self;

		if ((isAccountVerified.value && !title?.content?.fullPath) || overrideRedirectUrl) {
			let overrideSuccessUrl: string | null = null;

			if (overrideRedirectUrl) {
				overrideSuccessUrl = getTitlePlayerUrl({
					fullPath: title.content.fullPath,
					linkContextId,
					expPromoCode: expPromoCode?.code,
					fullUrl: true,
					packageShortName: offer.package.shortName,
				});
			}

			openJwTvRentalModal({
				modalProps: {
					title: title!,
					offer,
					additionalContexts: additionalContexts.map(el =>
						transformContextToLinkToContext(el)
					) as S72ModalContexts,
					overrideSuccessUrl,
					promoCode: expPromoCode?.code,
				},
				onClose,
			});
		} else {
			const titlePlayerUrl = getTitlePlayerUrl({
				fullPath: title.content.fullPath,
				linkContextId,
				expPromoCode: expPromoCode?.code,
				packageShortName: offer.package.shortName,
			});
			if (titlePlayerUrl) {
				router.push(titlePlayerUrl);
			}
		}
	}

	return {
		openRentalModal,
	};
}
